/**
 * Cost figures for the human FAQ — computed from prices.json, never typed.
 *
 * WHY THIS EXISTS
 * ---------------
 * The 2026-08-19 GEO monitor found Roamzy absent from every answer-engine
 * result on cost intent ("how much does an eSIM for an AI agent cost") while
 * competitors got quoted with concrete figures. The cause was not authority —
 * engines cite us verbatim on crypto/anon intent — it was that nothing of ours
 * ANSWERED a cost question. All six `pricing` entries in data/faq.json
 * described the MECHANICS of paying (how charging works, the minimum, expiry,
 * premium chips, refunds, promo codes); none said what it costs.
 *
 * The answers that close that gap are full of numbers, and data/faq.json is
 * static JSON that four surfaces read (SPA route, SSR body, FAQPage JSON-LD,
 * /faq.md). Typing the numbers in would have put a stale price in an answer
 * engine's mouth on the next tariff change — the exact failure the operator has
 * been burned by repeatedly. So the JSON carries `{{tokens}}` and every surface
 * substitutes at render time from this module.
 *
 * It lives in `shared` rather than `backend/util` because BOTH sides render the
 * same catalog: the backend injects the SSR body and the JSON-LD, the frontend
 * SPA re-renders the page on hydration. A backend-only helper would have left
 * the visible page showing raw `{{gb_per_min_spain}}` after hydration, or
 * forced a second copy of these formulas into the frontend — and a copy of a
 * source of truth is how the truth stops being single. Each side passes its own
 * already-loaded catalog in (backend/src/data/prices.ts,
 * frontend/src/data/prices.ts); the arithmetic exists once, here.
 *
 * ROUNDING — every convention below is one the repo already uses:
 *   - A PUBLISHED rate renders `toFixed(2)` per GB, the same as
 *     scripts/seo/sync-content-rates.mjs `gbStr`, so a rate quoted in the FAQ
 *     matches the country page character for character.
 *   - A DERIVED cost ("3 GB in Japan") renders `(qty * rate).toFixed(2)`, the
 *     same as a `data-rate="esim-japan:gb*3:2"` span in a blog body — so the
 *     FAQ and the article cannot disagree about the same worked example.
 *   - A VOLUME the minimum top-up buys floors to 1 dp, exactly as
 *     backend/util/region-body.ts `gb1()` does, so /faq and /regions/* state
 *     the same "what $20 buys" figure and neither promises data the money does
 *     not cover.
 *   - A COUNT of days or weeks a top-up lasts floors, for the same reason.
 *
 * USAGE ENVELOPES (FAQ_USAGE) are not prices and cannot come from prices.json.
 * They are our own published field-guide figures; each carries the article that
 * states it, and backend/src/__tests__/faq-cost-answers.test.ts asserts the
 * article still says so — change the guide and the gate fires.
 */
import { USDT_DECIMALS, WALLET_MIN_TOPUP_MICRO_USDT } from './wallet.js';
/**
 * The anti-dust minimum, from the wallet constant the dashboard enforces —
 * not a literal. Operator-locked at $20; stated here only so the prose and the
 * enforcement point cannot drift.
 */
export const FAQ_MIN_TOPUP_USDT = WALLET_MIN_TOPUP_MICRO_USDT / 10 ** USDT_DECIMALS;
/**
 * Published usage envelopes — first-party, from our own field guides.
 * Sources (data/articles.json):
 *   weekLowGb / weekHighGb    blog/how-much-data-do-you-need
 *                             "For a typical 7-day trip, 1.5–3 GB … is enough"
 *   nomadWeek{Low,High}Gb     same article, Traveler Type Quick Guide:
 *                             "Digital nomad (coworking + café Wi-Fi) 1–4 GB"
 *   hdVideoHourGb             blog/how-much-data-do-apps-use "HD (1080p): ~3 GB/hr"
 *   videoCallHourMb           same article, "around 300 MB/hr" (WhatsApp video)
 *   agentLightMbDay           blog/ai-agent-data-usage worked example:
 *                             200 API calls ≈ 4 MB + 100 text fetches ≈ 30 MB
 *   agentHeavyMbDay           same article, "the 100 fetches balloon toward ~250 MB"
 */
export const FAQ_USAGE = {
    weekLowGb: 1.5,
    weekHighGb: 3,
    nomadWeekLowGb: 1,
    nomadWeekHighGb: 4,
    hdVideoHourGb: 3,
    videoCallHourMb: 300,
    agentLightMbDay: 34,
    agentHeavyMbDay: 250,
};
/** The "cheap country" line the catalog-shape answer draws, in $/GB. */
export const FAQ_CHEAP_THRESHOLD_GB = 1;
/** Package-utilisation illustrations — pure ratios, no external price. */
export const FAQ_PACK_UTILISATION = [0.75, 0.5];
/** The five-country European route the multi-country answer walks. */
export const FAQ_EU5_SLUGS = [
    'esim-spain',
    'esim-france',
    'esim-italy',
    'esim-switzerland',
    'esim-germany',
];
// ── formatters ───────────────────────────────────────────────────────────────
/** Money, 2 dp — the site-wide display convention for a per-GB figure. */
const usd = (v) => `$${v.toFixed(2)}`;
const usdRange = (lo, hi) => `${usd(lo)}–${usd(hi)}`;
/** Drop a trailing ".0" so 3.0 reads "3" and 1.5 stays "1.5". */
const trim1 = (v) => v.toFixed(1).replace(/\.0$/, '');
/** A published usage envelope, rendered as-is. */
const gbPlain = (v) => `${trim1(v)} GB`;
const gbPlainRange = (lo, hi) => `${trim1(lo)}–${trim1(hi)} GB`;
/** A volume some amount of money buys — floored, never rounded up. */
const gbBought = (v) => `${trim1(Math.floor(v * 10) / 10)} GB`;
/** A whole count of days/weeks — floored, and never below one. */
const spanCount = (v) => String(Math.max(1, Math.floor(v)));
/**
 * A wide catalog band: "$0.61–$48". Rounds the top like a human would, the
 * same rule as RATE_RANGES.regularGb — only the separator differs, because
 * prose takes an en dash and the machine surfaces already ship a hyphen.
 */
const gbBand = (lo, hi) => `$${lo.toFixed(2)}–$${hi >= 10 ? Math.round(hi).toString() : hi.toFixed(2)}`;
const pct = (v) => `${Math.round(v * 100)}%`;
// ── figure builder ───────────────────────────────────────────────────────────
const median = (sorted) => {
    const n = sorted.length;
    if (n === 0)
        return 0;
    const mid = Math.floor(n / 2);
    if (n % 2 === 1)
        return sorted[mid];
    return ((sorted[mid - 1] + sorted[mid]) / 2);
};
/** Per-GB rate of a row, computed when the catalog only carries per-MB. */
export function faqRateGb(row) {
    return row.rateUsdtPerGb ?? row.rateUsdtPerMb * 1024;
}
/** The shape of the whole catalog, as the FAQ describes it. */
function catalogStats(rows) {
    const regular = rows.filter((r) => !r.isPremium);
    const premium = rows.filter((r) => r.isPremium);
    if (regular.length === 0)
        throw new Error('faq-figures: no regular countries');
    const regularGb = regular.map(faqRateGb);
    const floor = Math.min(...regularGb);
    return {
        regular,
        premium,
        floor,
        ceiling: Math.max(...regularGb),
        // Float equality on a rate is a trap — the catalog stores 0.0006 * 1024.
        atFloor: regular.filter((r) => Math.abs(faqRateGb(r) - floor) < 1e-9),
        dearest: regular.reduce((a, b) => (faqRateGb(b) > faqRateGb(a) ? b : a)),
        med: median([...regularGb].sort((a, b) => a - b)),
        underCheap: regular.filter((r) => faqRateGb(r) < FAQ_CHEAP_THRESHOLD_GB),
        floorMb: Math.min(...regular.map((r) => r.rateUsdtPerMb)),
    };
}
function build(rows) {
    const bySlug = new Map(rows.map((r) => [r.slug, r]));
    /** Throws on a slug this file names but the catalog no longer carries. */
    const rate = (slug) => {
        const r = bySlug.get(slug);
        if (!r)
            throw new Error(`faq-figures: prices.json has no "${slug}"`);
        return faqRateGb(r);
    };
    const rateMb = (slug) => {
        const r = bySlug.get(slug);
        if (!r)
            throw new Error(`faq-figures: prices.json has no "${slug}"`);
        return r.rateUsdtPerMb;
    };
    const { regular, premium, floor, ceiling, atFloor, dearest, med, underCheap, floorMb } = catalogStats(rows);
    const min = FAQ_MIN_TOPUP_USDT;
    const u = FAQ_USAGE;
    const eu5 = FAQ_EU5_SLUGS.map((s) => rate(s));
    const eu5Low = Math.min(...eu5);
    const eu5High = Math.max(...eu5);
    const spain = rate('esim-spain');
    const thailand = rate('esim-thailand');
    const japan = rate('esim-japan');
    const usa = rate('esim-united-states');
    const turkey = rate('esim-turkey');
    const utilA = FAQ_PACK_UTILISATION[0];
    const utilB = FAQ_PACK_UTILISATION[1];
    const premiumSorted = [...premium].sort((a, b) => faqRateGb(b) - faqRateGb(a));
    const premiumNames = premiumSorted.map((c) => c.name);
    const nameList = (xs) => xs.length <= 1
        ? xs[0] ?? ''
        : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
    const agentLightDayUsa = u.agentLightMbDay * rateMb('esim-united-states');
    const agentHeavyDayUsa = u.agentHeavyMbDay * rateMb('esim-united-states');
    return {
        // ── the catalog, as a shape ──
        min_topup: `$${min}`,
        min_topup_usdt: `${min} USDT`,
        country_count: String(rows.length),
        regular_count: String(regular.length),
        regular_gb_range: gbBand(floor, ceiling),
        floor_gb: usd(floor),
        floor_count: String(atFloor.length),
        median_gb: usd(med),
        max_regular_gb: usd(faqRateGb(dearest)),
        max_regular_country: dearest.name,
        under_one_count: String(underCheap.length),
        under_one_threshold: `$${FAQ_CHEAP_THRESHOLD_GB}`,
        premium_count: String(premium.length),
        premium_names: nameList(premiumNames),
        premium_gb_range: premium.length
            ? gbBand(Math.min(...premium.map(faqRateGb)), Math.max(...premium.map(faqRateGb)))
            : '',
        // ── published usage envelopes ──
        week_gb_range: gbPlainRange(u.weekLowGb, u.weekHighGb),
        fortnight_gb_range: gbPlainRange(u.weekLowGb * 2, u.weekHighGb * 2),
        nomad_week_gb_range: gbPlainRange(u.nomadWeekLowGb, u.nomadWeekHighGb),
        nomad_month_gb_range: gbPlainRange(u.nomadWeekLowGb * 4, u.nomadWeekHighGb * 4),
        hd_hour_gb: gbPlain(u.hdVideoHourGb),
        video_hour_mb: `${u.videoCallHourMb} MB`,
        agent_light_mb_day: `${u.agentLightMbDay} MB`,
        agent_heavy_mb_day: `${u.agentHeavyMbDay} MB`,
        // ── what a week costs ──
        week_cost_spain: usdRange(u.weekLowGb * spain, u.weekHighGb * spain),
        week_cost_thailand: usdRange(u.weekLowGb * thailand, u.weekHighGb * thailand),
        week_cost_japan: usdRange(u.weekLowGb * japan, u.weekHighGb * japan),
        week_cost_usa: usdRange(u.weekLowGb * usa, u.weekHighGb * usa),
        // ── what the minimum buys ──
        gb_per_min_spain: gbBought(min / spain),
        gb_per_min_turkey: gbBought(min / turkey),
        gb_per_min_thailand: gbBought(min / thailand),
        gb_per_min_japan: gbBought(min / japan),
        gb_per_min_usa: gbBought(min / usa),
        gb_per_min_floor: gbBought(min / floor),
        gb_per_min_median: gbBought(min / med),
        weeks_per_min_spain: spanCount(min / (u.weekHighGb * spain)),
        weeks_per_min_usa: spanCount(min / (u.weekHighGb * usa)),
        hd_hour_cost_japan: usd(u.hdVideoHourGb * japan),
        // ── a five-country European fortnight ──
        eu5_rate_range: usdRange(eu5Low, eu5High),
        eu5_cost_low: usd(u.weekLowGb * 2 * eu5Low),
        eu5_cost_high: usd(u.weekHighGb * 2 * eu5High),
        // ── a month of remote work ──
        month_cost_thailand: usdRange(u.nomadWeekLowGb * 4 * thailand, u.nomadWeekHighGb * 4 * thailand),
        month_cost_spain: usdRange(u.nomadWeekLowGb * 4 * spain, u.nomadWeekHighGb * 4 * spain),
        month_cost_japan: usdRange(u.nomadWeekLowGb * 4 * japan, u.nomadWeekHighGb * 4 * japan),
        video_hour_cost_thailand: usd(u.videoCallHourMb * rateMb('esim-thailand')),
        // ── metered vs a fixed package ──
        pack_util_a: pct(utilA),
        pack_overpay_a: pct(1 / utilA - 1),
        pack_util_b: pct(utilB),
        pack_overpay_b: pct(1 / utilB - 1),
        rate_thailand: usd(thailand),
        rate_japan: usd(japan),
        rate_usa: usd(usa),
        // ── what an agent spends ──
        agent_light_day_usa: usd(agentLightDayUsa),
        agent_light_day_floor: usd(u.agentLightMbDay * floorMb),
        agent_light_days_usa: spanCount(min / agentLightDayUsa),
        agent_heavy_day_usa: usd(agentHeavyDayUsa),
        agent_heavy_days_usa: spanCount(min / agentHeavyDayUsa),
    };
}
/**
 * Every FAQ figure, keyed by token name.
 *
 * Returns an EMPTY map if the catalog cannot support the figures (a named
 * country gone from prices.json, an empty catalog). That is deliberate: with
 * no figures every cost answer fails to resolve and is dropped from the page
 * entirely by `resolveFaqPair` below. A missing answer is recoverable; a
 * wrong price in an answer engine's index is not.
 */
export function buildFaqFigures(rows) {
    try {
        return Object.freeze(build(rows));
    }
    catch {
        return Object.freeze({});
    }
}
/** `{{token}}` — lowercase, digits and underscores only. */
const TOKEN_SOURCE = '\\{\\{([a-z0-9_]+)\\}\\}';
/** Substitute every known token; leave unknown ones in place for the caller. */
export function resolveFaqTokens(text, figures) {
    return text.replace(new RegExp(TOKEN_SOURCE, 'g'), (whole, name) => Object.prototype.hasOwnProperty.call(figures, name) ? figures[name] : whole);
}
/** Token names still unsubstituted in a rendered string. */
export function unresolvedFaqTokens(text) {
    return [...text.matchAll(new RegExp(TOKEN_SOURCE, 'g'))].map((m) => m[1]);
}
/**
 * Resolve one Q&A. Returns null when any token in either half is unknown —
 * the caller drops the entry rather than publishing `{{gb_per_min_spain}}` or,
 * worse, half an answer.
 */
export function resolveFaqPair(q, a, figures) {
    const rq = resolveFaqTokens(q, figures);
    const ra = resolveFaqTokens(a, figures);
    if (unresolvedFaqTokens(rq).length || unresolvedFaqTokens(ra).length)
        return null;
    return { q: rq, a: ra };
}
